import { z } from 'zod';
import type { ThemeTokens } from '../theme';

/**
 * Zod mirror of `ThemeTokens` (see `../theme.ts`) for the super-admin
 * `PATCH /admin/tenant/theme` route. Every field optional (a tenant overrides
 * token-by-token; omitted falls through to baseline). Colors are HSL triplets,
 * fonts/gradients are raw CSS, logos are URLs — all validated only for length +
 * shape, not exact format, so the typed admin editor stays the real guard.
 */
const hsl = z.string().max(64);
const css = z.string().max(400);
const url = z.string().max(2048);

/**
 * The CSS-var half of the theme — shared by the top-level schema and the `dark`
 * sub-object, so a token can never be settable in one mode but not the other.
 */
const modeShape = {
  primary: hsl.optional(),
  primaryForeground: hsl.optional(),
  accent: hsl.optional(),
  accentForeground: hsl.optional(),
  ring: hsl.optional(),
  background: hsl.optional(),
  foreground: hsl.optional(),
  card: hsl.optional(),
  cardForeground: hsl.optional(),
  border: hsl.optional(),
  radius: z.string().max(32).optional(),
  fontSans: css.optional(),
  secondary: hsl.optional(),
  secondaryForeground: hsl.optional(),
  muted: hsl.optional(),
  mutedForeground: hsl.optional(),
  popover: hsl.optional(),
  popoverForeground: hsl.optional(),
  input: hsl.optional(),
  success: hsl.optional(),
  successForeground: hsl.optional(),
  fontHeading: css.optional(),
  fontBody: css.optional(),
  brandDark: hsl.optional(),
  brandLight: hsl.optional(),
  highlight: hsl.optional(),
  highlightForeground: hsl.optional(),
  pv: hsl.optional(),
  pvBg: hsl.optional(),
  pvBorder: hsl.optional(),
  brandGradient: css.optional(),
  memberGradient: css.optional(),
  goldGradient: css.optional(),
  // Learning (LMS) palette — see the `lms*` block in `../theme.ts`.
  lmsBrand: hsl.optional(),
  lmsBrandDark: hsl.optional(),
  lmsBrandDeep: hsl.optional(),
  lmsBrandLight: hsl.optional(),
  lmsGold: hsl.optional(),
  lmsGoldHover: hsl.optional(),
  lmsGoldDeep: hsl.optional(),
  lmsGoldTint: hsl.optional(),
  lmsPaper: hsl.optional(),
  lmsInk: hsl.optional(),
  lmsMuted: hsl.optional(),
  lmsHairline: hsl.optional(),
  lmsCream: hsl.optional(),
  lmsFontHeading: css.optional(),
};

export const themeTokensSchema = z.object({
  ...modeShape,
  // Assets + font hrefs: mode-invariant, so they live outside `modeShape` and are
  // not settable inside `dark` (a dark-surface logo has its own token).
  logoUrl: url.optional(),
  fontLinks: z.array(url).max(10).optional(),
  wordmark: z.string().max(120).optional(),
  logoUrlDark: url.optional(),
  logoKey: z.string().max(512).optional(),
  logoKeyDark: z.string().max(512).optional(),
  faviconKey: z.string().max(512).optional(),
  /** Dark-mode overrides, merged over the light set — see `resolveThemeForMode`. */
  dark: z.object(modeShape).optional(),
});

/** Compile-time lockstep: schema inference must match `ThemeTokens` both ways. */
type _SchemaToType = z.infer<typeof themeTokensSchema> extends ThemeTokens ? true : never;
type _TypeToSchema = ThemeTokens extends z.infer<typeof themeTokensSchema> ? true : never;
const _schemaMatchesType: _SchemaToType = true;
const _typeMatchesSchema: _TypeToSchema = true;
void _schemaMatchesType;
void _typeMatchesSchema;
